'use client';

/**
 * Lang switcher v2 — toggles between EN and FR, preserves the current pathKey.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-014
 */

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils';

const LOCALES = ['en', 'fr'] as const;

export function LangSwitcherV2({ className }: { className?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();

  return (
    <div
      role="group"
      aria-label="Language switcher"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 p-0.5',
        className
      )}
    >
      {LOCALES.map((loc) => {
        const isActive = loc === currentLocale;
        return (
          <button
            key={loc}
            type="button"
            aria-pressed={isActive}
            aria-label={`Switch to ${loc === 'en' ? 'English' : 'Français'}`}
            onClick={() => {
              if (!isActive) {
                router.replace(pathname, { locale: loc });
              }
            }}
            className={cn(
              'h-7 min-w-[36px] rounded-full px-2 text-xs font-medium uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500',
              isActive
                ? 'bg-cyan-500 text-ink-950 shadow-glow-cyan-sm'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            )}
          >
            {loc}
          </button>
        );
      })}
    </div>
  );
}
